import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { motion } from "framer-motion";
import ParticleNetwork from "./ParticleNetwork";
import CircuitBoard from "./CircuitBoard";
import GlowingOrb from "./GlowingOrb";
import AnimatedCounter from "./AnimatedCounter";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-12 md:pb-0 w-full">
      {/* Particle Network */}
      <ParticleNetwork />

      {/* Animated Grid Background */}
      <div className="absolute inset-0 neon-grid" />

      {/* Circuit Board SVGs */}
      <CircuitBoard className="hidden md:block top-10 left-0 w-[400px] h-[400px] opacity-40" />
      <CircuitBoard className="hidden md:block bottom-10 right-0 w-[350px] h-[350px] opacity-30 rotate-180" />


      {/* Radial Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-[120px] animate-neon-flicker" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-neon-violet/10 rounded-full blur-[100px]" />

      {/* Scan lines overlay */}
      <div className="absolute inset-0 scanlines pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center w-full">
        <div className="max-w-5xl mx-auto">

          {/* Main Headline with stagger */}
          <div className="mb-6">
            <motion.h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight font-display">
              {["Scale Your"].map((word, i) => (
                <motion.span
                  key={i}
                  className="text-foreground inline-block"
                  initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.1 }}
                >
                  {word}
                </motion.span>
              ))}
              <motion.span
                className="gradient-text block neon-glow"
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 100 }}
              >
                Ad Campaigns
              </motion.span>
              <motion.span
                className="gradient-accent-text inline-block"
                initial={{ opacity: 0, x: -30, filter: "blur(10px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.7, delay: 0.5 }}
              >
                Limitlessly
              </motion.span>
            </motion.h1>
          </div>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-lg md:text-xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Get instant access to premium agency ad accounts for Meta, TikTok, Snapchat, Pinterest, and 20+ platforms.
            Experience unrestricted spending, compliant advertising, and dedicated support.
          </motion.p>

          {/* Stats with animated counters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12"
          >
            {[
              { value: "20+", label: "Platforms" },
              { value: "$30M+", label: "Ad Spend Managed" },
              { value: "24/7", label: "Expert Support" },
            ].map((stat, i) => (
              <div key={stat.label} className="flex items-center gap-8">
                {i > 0 && <div className="hidden md:block w-px h-12 bg-neon-cyan/20" />}
                <div className="text-center">
                  <AnimatedCounter
                    target={stat.value}
                    className="text-3xl font-bold font-display gradient-text neon-glow"
                  />
                  <div className="text-sm text-muted-foreground tracking-wide">{stat.label}</div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* CTA Button with pulse effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="flex justify-center"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                variant="hero"
                size="xl"
                className="group relative overflow-hidden"
                onClick={() => window.location.href = '/#contact'}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Started Now
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom neon line */}
      <div className="absolute bottom-0 left-0 right-0 neon-line" />
    </section>
  );
};

export default Hero;
